import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Reveal, Stagger, StaggerItem } from "../motion/Reveal";
import { LAWS } from "../../data/laws";
import { DAILY_PRACTICES } from "../../data/dailyLife";

const Label = ({ id, children }) => (
  <p id={id} className="eyebrow scroll-mt-32">
    {children}
  </p>
);

const RelatedChip = ({ to, number, title, testId }) => (
  <Link
    to={to}
    data-testid={testId}
    className="group inline-flex items-center gap-3 rounded-full border hairline bg-white py-2.5 pl-4 pr-3 text-sm text-ink transition-colors duration-300 hover:border-terracotta hover:bg-terracotta hover:text-parchment"
  >
    <span className="font-mono text-[10px] tracking-[0.2em] text-terracotta transition-colors group-hover:text-parchment/80">
      {number}
    </span>
    {title}
    <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:rotate-45" />
  </Link>
);

export const GoDeeper = ({ law }) => {
  const { deeper } = law;
  const relatedLaws = deeper.relatedLaws.map((slug) => LAWS.find((l) => l.slug === slug));
  const relatedPractices = deeper.relatedPractices
    .map((id) => DAILY_PRACTICES.find((p) => p.id === id))
    .filter(Boolean);

  return (
    <section data-testid="law-go-deeper" className="mt-24 border-t hairline pt-16">
      <Reveal>
        <Label id="deeper">Go deeper</Label>
        <h2 className="mt-4 font-serif text-4xl leading-[1.05] tracking-tight sm:text-5xl">
          Inside the heading.{" "}
          <span className="italic text-ink-soft">What this law actually covers.</span>
        </h2>
      </Reveal>

      <Stagger className="mt-12 grid grid-cols-1 gap-px bg-ink/10 sm:grid-cols-2" stagger={0.06}>
        {deeper.details.map((d, i) => (
          <StaggerItem key={d.title}>
            <div data-testid={`law-detail-topic-${i + 1}`} className="h-full bg-parchment p-6 sm:p-8">
              <span className="font-mono text-[10px] tracking-[0.22em] text-terracotta">
                {String(i + 1).padStart(2, "0")}
              </span>
              <h3 className="mt-3 font-serif text-2xl leading-snug text-ink">{d.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-ink-soft">{d.body}</p>
            </div>
          </StaggerItem>
        ))}
      </Stagger>

      <Reveal className="mt-20">
        <Label>Classical sources to read</Label>
        <ol data-testid="law-sources-list" className="mt-6 border-t hairline">
          {deeper.sources.map((s, i) => (
            <li
              key={s.ref}
              data-testid={`law-source-${i + 1}`}
              className="grid grid-cols-1 gap-2 border-b hairline py-5 sm:grid-cols-[minmax(0,16rem)_1fr] sm:gap-8"
            >
              <span className="font-mono text-[11px] uppercase leading-relaxed tracking-[0.18em] text-ink">
                {s.ref}
              </span>
              <p className="text-sm leading-relaxed text-ink-soft sm:text-base">{s.note}</p>
            </li>
          ))}
        </ol>
      </Reveal>

      <Reveal className="mt-20">
        <Label>Related</Label>
        <div className="mt-6 grid grid-cols-1 gap-10 sm:grid-cols-2">
          <div>
            <p className="text-sm text-ink-muted">Other laws that touch this one</p>
            <div className="mt-4 flex flex-wrap gap-2.5">
              {relatedLaws.map((l) => (
                <RelatedChip
                  key={l.slug}
                  to={`/laws/${l.slug}`}
                  number={`0${l.number}`}
                  title={l.title}
                  testId={`law-related-law-${l.slug}`}
                />
              ))}
            </div>
          </div>
          <div>
            <p className="text-sm text-ink-muted">Daily practices that grow from it</p>
            <div className="mt-4 flex flex-wrap gap-2.5">
              {relatedPractices.map((p) => (
                <RelatedChip
                  key={p.id}
                  to={`/daily-life#${p.id}`}
                  number={p.number}
                  title={p.title}
                  testId={`law-related-practice-${p.id}`}
                />
              ))}
            </div>
          </div>
        </div>
      </Reveal>

      <Reveal className="mt-20">
        <div data-testid="law-reflection" className="relative overflow-hidden bg-ink px-8 py-12 text-parchment sm:px-12 sm:py-16">
          <span className="pointer-events-none absolute -right-6 -top-10 font-serif text-[14rem] leading-none text-parchment/[0.06]">
            ?
          </span>
          <p className="eyebrow">A reflection to sit with</p>
          <p className="relative mt-6 max-w-2xl font-serif text-2xl italic leading-snug sm:text-3xl lg:text-4xl">
            {deeper.reflection}
          </p>
        </div>
      </Reveal>
    </section>
  );
};
